import React from 'react';
import {
    Button,
    Dialog,
    DialogActions,
    DialogBody,
    DialogContent,
    DialogSurface,
    DialogTitle,
    DialogTrigger,
    Text,
    Textarea,
    makeStyles,
    mergeClasses,
    shorthands,
} from '@fluentui/react-components';
import { Dismiss24Regular } from '@fluentui/react-icons';
import { customColors, customFonts } from '../../../../../styles';
import type { UseSavedPromptVm } from '../../../hooks/useSavedPrompt';

const useStyles = makeStyles({
    promptDialog: {
        width: '560px',
        ...shorthands.padding('0px'),
    },
    titleRow: {
        display: 'grid',
        gridTemplateColumns: '32px 1fr 32px',
        alignItems: 'start',
        width: '100%',
        boxSizing: 'border-box',
        ...shorthands.padding('20px', '20px', '8px', '20px'),
    },
    titleSpacer: {
        width: '32px',
        height: '32px',
    },
    titleBlock: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        textAlign: 'center',
        gap: '4px',
        width: '100%',
        boxSizing: 'border-box',
        ...shorthands.padding('0px', '32px'),
    },
    title: {
        fontFamily: customFonts.Lato,
        fontSize: '22px',
        fontWeight: '700',
        color: customColors.protivitiDarkBlue,
    },
    closeDialogButton: {
        minWidth: '28px',
        height: '28px',
        justifySelf: 'end',
    },
    promptDialogText: {
        fontFamily: customFonts.Lato,
        fontSize: '14px',
        color: '#5C6B7A',
    },
    promptDialogContent: {
        display: 'flex',
        flexDirection: 'column',
        gap: '12px',
        ...shorthands.padding('8px', '20px', '20px', '20px'),
    },
    promptDialogInput: {
        minHeight: '140px',
        fontFamily: customFonts.Lato,
    },
    promptDialogActions: {
        display: 'flex',
        justifyContent: 'space-between',
        gap: '8px',
    },
    cancelButton: {
        fontFamily: customFonts.Lato,
        fontSize: '13px',
        fontWeight: '600',
        minWidth: '130px',
    },
    primaryButton: {
        fontFamily: customFonts.Lato,
        fontSize: '13px',
        fontWeight: '600',
        minWidth: '130px',
        ...shorthands.border('1px', 'solid', customColors.protivitiOrange),
        backgroundColor: customColors.protivitiOrange,
        color: customColors.white,
    },
    deletePromptDialog: {
        width: '450px',
        borderRadius: '15px',
    },
    deletePromptTitle: {
        fontFamily: customFonts.TitleFont,
        fontWeight: '500',
        fontSize: '24px',
    },
    deletePromptActions: {
        paddingTop: '10%',
    },
    deletePromptDelete: {
        backgroundColor: customColors.protivitiOrange,
        '&:hover': {
            backgroundColor: customColors.summarizeButtonDarker,
            color: customColors.white,
        },
    },
    deletePromptClose: {
        backgroundColor: customColors.white,
        '&:hover': {
            backgroundColor: customColors.summarizeButtonDarker,
            color: customColors.white,
        },
    },
    deletePromptButton: {
        fontFamily: customFonts.Lato,
        fontSize: '12px',
        borderRadius: '5px',
        ...shorthands.border('1.5px', 'solid', customColors.protivitiOrange),
    },
});

interface SavedPromptDialogsProps {
    vm: UseSavedPromptVm;
}

const SavedPromptDialogs: React.FC<SavedPromptDialogsProps> = ({ vm }) => {
    const styles = useStyles();

    return (
        <>
            <Dialog open={vm.isPromptDialogOpen}>
                <DialogSurface className={styles.promptDialog}>
                    <DialogBody>
                        <DialogTitle>
                            <div className={styles.titleRow}>
                                <div className={styles.titleSpacer} />
                                <div className={styles.titleBlock}>
                                    <Text className={styles.title}>
                                        {vm.editingPrompt ? 'Edit Saved Prompt' : 'Save New Prompt'}
                                    </Text>
                                    <Text className={styles.promptDialogText}>
                                        {vm.editingPrompt
                                            ? 'Update the saved prompt text below.'
                                            : 'Save a reusable prompt for this chat context.'}
                                    </Text>
                                </div>
                                <Button
                                    className={styles.closeDialogButton}
                                    appearance="subtle"
                                    icon={<Dismiss24Regular />}
                                    aria-label="Close save prompt dialog"
                                    onClick={vm.closePromptDialog}
                                    disabled={vm.isPromptSaving}
                                />
                            </div>
                        </DialogTitle>
                        <DialogContent className={styles.promptDialogContent}>
                            <Textarea
                                className={styles.promptDialogInput}
                                placeholder="Type the prompt you want to save..."
                                value={vm.promptDialogValue}
                                onChange={(_, data) => vm.setPromptDialogValue(data.value)}
                            />
                            <div className={styles.promptDialogActions}>
                                <Button
                                    className={styles.cancelButton}
                                    appearance="secondary"
                                    onClick={vm.closePromptDialog}
                                    disabled={vm.isPromptSaving}
                                >
                                    Cancel
                                </Button>
                                <Button
                                    className={styles.primaryButton}
                                    appearance="primary"
                                    onClick={() => void vm.savePrompt()}
                                    disabled={vm.isPromptSaving || vm.promptDialogValue.trim().length === 0}
                                >
                                    {vm.isPromptSaving
                                        ? 'Saving...'
                                        : vm.editingPrompt
                                          ? 'Save Changes'
                                          : 'Save Prompt'}
                                </Button>
                            </div>
                        </DialogContent>
                    </DialogBody>
                </DialogSurface>
            </Dialog>

            <Dialog open={vm.isDeletePromptDialogOpen} modalType="alert">
                <DialogSurface className={styles.deletePromptDialog}>
                    <DialogBody>
                        <DialogTitle className={styles.deletePromptTitle}>
                            Are you sure you want to delete this saved prompt?
                        </DialogTitle>
                        <DialogActions className={styles.deletePromptActions}>
                            <DialogTrigger action="close" disableButtonEnhancement>
                                <Button
                                    className={mergeClasses(styles.deletePromptClose, styles.deletePromptButton)}
                                    appearance="secondary"
                                    disabled={vm.isPromptSaving}
                                    onClick={vm.closeDeletePromptDialog}
                                >
                                    Cancel
                                </Button>
                            </DialogTrigger>
                            <DialogTrigger action="close" disableButtonEnhancement>
                                <Button
                                    className={mergeClasses(styles.deletePromptDelete, styles.deletePromptButton)}
                                    onClick={() => void vm.deletePrompt()}
                                    disabled={vm.isPromptSaving || !vm.promptPendingDelete}
                                >
                                    {vm.isPromptSaving ? 'Deleting...' : 'Delete'}
                                </Button>
                            </DialogTrigger>
                        </DialogActions>
                    </DialogBody>
                </DialogSurface>
            </Dialog>
        </>
    );
};

export default SavedPromptDialogs;
